import React, { useEffect, useState } from "react";
import {
  Flex,
  Text,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  SkeletonText,
  Button,
} from "@chakra-ui/react";
import { dateFormat, downloadCSVFileFromBase64, FORMATS, getOrganisationMembership, getRowIndex } from "utils";
import Pagination from "components/Pagination";
import {
  AccountStatus,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  MerchantViewRef,
  BackLink,
  Roles
} from "common";
import { GetOrganizationsRequest } from "redux/services/membership/types";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { CSVFile } from "components/Icons";
import { useGetReconciliationReportQuery, useGetReconciliationSummariesQuery } from "redux/services/reconciliation";
import { FiArrowLeft } from "react-icons/fi";
import queryString from "query-string";
import { useGetProfileQuery } from "redux/services/membership";
import { ReconciliationReport } from "redux/services/reconciliation/types";
import { useGetDocumentMutation } from "redux/services/documentRepository";
import { useGetUserRole } from "hooks/useGetUserRole";

const ReconciliationSummary: React.FC = () => {
  const { IdReconciliate } = useParams() as any;
  const location = useLocation();
  const queryParams = queryString.parse(location.search);
  const from = queryParams.from as string;
  const { push } = useHistory();
  const { data: profileData } = useGetProfileQuery();
  const membershipOrganisation = getOrganisationMembership(profileData?.data?.memberships);
  const orgToken = membershipOrganisation?.token ?? "";

  const { isEnterprisePaymentOperation, isEnterpriseBankAdmin } = useGetUserRole();

  const [getOrganisationsParamsRequest, setGetOrganisationsParamsRequest] =
    useState<
      GetOrganizationsRequest & {
        status?: AccountStatus;
        createdFrom?: string;
        createdTo?: string;
      }
    >({
      pageNum: DEFAULT_PAGE,
      pageSize: DEFAULT_PAGE_SIZE,
      orgRole: "TPPP",
      status: AccountStatus.ACTIVE,
      sort: "-createdAt",
    });

  const {
    data: reconciliationSummariesData,
    isFetching: isGetReconciliationSummariesFetching,
    isLoading: isLoadingReconciliationSummaries,
  } = useGetReconciliationSummariesQuery({
    id: IdReconciliate
  }, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: reconciliationReportData
  } = useGetReconciliationReportQuery({
    id: IdReconciliate
  }, {
    refetchOnMountOrArgChange: false,
  });

  const [
    getDocument,
    {
      data: documentDetail,
      error: documentError,
    },
  ] = useGetDocumentMutation();

  useEffect(() => {
    if (documentDetail && !documentError) {
      downloadCSVFileFromBase64(documentDetail);
    }
  }, [documentDetail, documentError]);

  const onDownloadClick = (document: ReconciliationReport) => {
    getDocument({
      documentId: document.documentId,
      orgToken: orgToken,
    });
  };

  const isLoading = isLoadingReconciliationSummaries || isGetReconciliationSummariesFetching;
  const orgId = reconciliationSummariesData?.data?.length && reconciliationSummariesData?.data?.length > 0 && reconciliationSummariesData?.data?.[0].orgId;
  const document = reconciliationReportData?.data?.find(doc => doc.reportFormat === 'CSV' && doc.isDeleted === false);
  return (
    <Flex
      className="Users"
      w="100%"
      h="100%"
      p="20px"
      flexDir={"column"}
      position="relative"
      minHeight="calc(100vh - 124px)"
    >
      <Link className="flex w-full" to={BackLink[from]?.to || "/reconciliation"}>
        <FiArrowLeft /> {BackLink[from]?.label || "Back to Reconciliation Batches"}
      </Link>
      <Text textStyle={"h1"} fontWeight={"extrabold"} mt={"20px"} mb="10px">
        View Reconciliation Summary
      </Text>

      <div className="flex flex-row w-full justify-between gap-10 items-center">
        <div>
          <Text fontSize="14px" color="gray.500" fontWeight="bold">
            Reconciliation Date: {reconciliationSummariesData?.data?.[0]?.reconciliation.date ? dateFormat(reconciliationSummariesData?.data?.[0]?.reconciliation.date, true, FORMATS["YYYY-MM-DD"]) : "-"}
          </Text>
        </div>
        <div className="flex flex-row gap-2 items-center">
        {
          (isEnterpriseBankAdmin || isEnterprisePaymentOperation) && <Button
            data-testid="action-btn"
            fontSize="14px"
            colorScheme="blue"
            onClick={() => {
              orgId && push(`/tppp-detail-view/${orgId}?from=${MerchantViewRef.RECONCILIATION_SUMMARIES}&backId=${IdReconciliate}`);
            }}
          >
            View TPPP
          </Button>
        }

        {document && (
          <button
          type="button"
          data-testid="csv-icon"
          className="cursor-pointer"
          style={{ background: "none", border: "none", padding: 0, margin: 0 }}
          onClick={() => {
            document && onDownloadClick(document);
          }}
          aria-label="Export as CSV"
        >
          <CSVFile />
        </button>
        )}
        
        </div>
      </div>

      <TableContainer mt="20px">
        <Table data-testid="table-container" variant="striped">
          <Thead backgroundColor={"yellow.500"}>
            <Tr>
              <Th isNumeric color={"white"} w={"75px"}>
                No.
              </Th>
              <Th color={"white"} w={"280px"}>
                Batch
              </Th>

              <Th color={"white"}>Reconciliation Status</Th>
              <Th color={"white"}>Transaction Count</Th>
              <Th color={"white"}>Amount</Th>
            </Tr>
          </Thead>

          {isLoading && (
            <Tr>
              <Td colSpan={6}>
                <SkeletonText
                  data-testid="skeleton-text"
                  noOfLines={15}
                  spacing="10px"
                  p="10px 0"
                />
              </Td>
            </Tr>
          )}

          {!isLoading && (
            <Tbody>
              {reconciliationSummariesData?.data?.map((reconciliationSummary, index) => {

                return (
                  <Tr key={reconciliationSummary.id}>
                    <Td isNumeric>
                      {getRowIndex(
                        getOrganisationsParamsRequest.pageNum,
                        getOrganisationsParamsRequest.pageSize,
                        index
                      )}
                    </Td>
     
                    <Td whiteSpace={"pre-wrap"}>
                      <span className="capitalize">{(reconciliationSummary.reconciliation.batch.code as string).replace('_', ' ')}</span>
                    </Td>
                    <Td>
                      {reconciliationSummary.reconStatus ?? "-"}
                    </Td>
                    <Td>
                      {reconciliationSummary.numberOfRecords ?? "0"}
                    </Td>
                    <Td>
                      {reconciliationSummary.amount.toFixed(2) ?? "0.00"} ZAR
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          )}
        </Table>
      </TableContainer>

      {!isLoading && reconciliationSummariesData?.data?.length === 0 && (
        <Text p="20px" textAlign="center" fontWeight="bold">
          No results found.
        </Text>
      )}

      {!isLoading && (
        <Flex align="center" justifyContent="end" marginTop={"10px"}>
          <Pagination
            totalRecords={reconciliationSummariesData?.paging?.totalRecords ?? 0}
            pageSize={getOrganisationsParamsRequest.pageSize}
            pageNum={getOrganisationsParamsRequest.pageNum}
            onChangePage={(pageNum) =>
              setGetOrganisationsParamsRequest({
                ...getOrganisationsParamsRequest,
                pageNum: pageNum,
              })
            }
          />
        </Flex>
      )}
    </Flex>
  );
};

export default ReconciliationSummary;